/**
 * Bộ trộn âm thanh đa luồng an toàn (Safe Multi-Source Audio Mixer) cho Web Interaction Recorder.
 * Kế thừa các cơ chế bảo vệ từ tour-recorder.ts:
 * 1. activeBridges Map chống Chromium V8 Garbage Collector thu hồi MediaStreamAudioSourceNode.
 * 2. Silent Keep-Alive Oscillator (gain = 0.00001) giữ nhịp timestamp liên tục cho MediaRecorder.
 * 3. Non-destructive HTMLMediaElement captureStream() + MutationObserver không làm mất tiếng loa ngoài.
 * 4. Hỗ trợ tùy chọn gộp thêm Micro của người dùng (includeMic) để thu trọn vẹn hội thoại 2 chiều với AI.
 */

export class WebRecordingAudioMixer {
    private audioContext: AudioContext | null = null;
    private audioDestination: MediaStreamAudioDestinationNode | null = null;
    private keepAliveOscillator: OscillatorNode | null = null;
    private keepAliveGain: GainNode | null = null;
    private activeBridges = new Map<AudioNode, { dest: MediaStreamAudioDestinationNode; source: MediaStreamAudioSourceNode }>();
    private activeStreamSources: MediaStreamAudioSourceNode[] = [];
    private connectedElements = new WeakSet<HTMLMediaElement>();
    private domObserver: MutationObserver | null = null;
    private audioCheckInterval: ReturnType<typeof setInterval> | null = null;
    private micStream: MediaStream | null = null;
    private hasDisplayAudio: boolean = false;

    public async initialize(displayStream: MediaStream, includeMic: boolean): Promise<MediaStreamTrack[]> {
        try {
            const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
            if (!AudioCtx) return [];

            this.audioContext = new AudioCtx({ sampleRate: 48000 });
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume().catch(() => {});
            }
            this.audioDestination = this.audioContext.createMediaStreamDestination();

            // 1. Silent Keep-Alive Oscillator (chống rớt khung âm thanh khi im lặng)
            this.keepAliveOscillator = this.audioContext.createOscillator();
            this.keepAliveGain = this.audioContext.createGain();
            this.keepAliveGain.gain.value = 0.00001;
            this.keepAliveOscillator.connect(this.keepAliveGain);
            this.keepAliveGain.connect(this.audioDestination);
            this.keepAliveOscillator.start();

            // 2. Nếu trình duyệt có cấp sẵn Tab Audio track từ getDisplayMedia, kết nối vào Mixer
            const displayAudioTracks = displayStream.getAudioTracks();
            this.hasDisplayAudio = displayAudioTracks.length > 0;
            if (this.hasDisplayAudio) {
                try {
                    const tabAudioStream = new MediaStream(displayAudioTracks);
                    const tabSource = this.audioContext.createMediaStreamSource(tabAudioStream);
                    tabSource.connect(this.audioDestination);
                    this.activeStreamSources.push(tabSource);
                } catch (e) {
                    console.warn('[WebRecordingAudioMixer] Không thể nối display audio track:', e);
                }
            }

            // 3. Nếu người dùng chọn Bật Micro, thu giọng nói từ Micro vào Mixer
            if (includeMic && typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia) {
                try {
                    this.micStream = await navigator.mediaDevices.getUserMedia({
                        audio: {
                            echoCancellation: true,
                            noiseSuppression: true,
                            autoGainControl: true
                        },
                        video: false
                    });
                    const micSource = this.audioContext.createMediaStreamSource(this.micStream);
                    micSource.connect(this.audioDestination);
                    this.activeStreamSources.push(micSource);
                } catch (micErr) {
                    console.warn('[WebRecordingAudioMixer] Không thể truy cập Micro người dùng, tiếp tục quay không có Micro:', micErr);
                }
            }

            // 4. Kết nối trực tiếp các nguồn âm thanh nội bộ (Gemini Live AudioStreamer, Howler, thẻ <audio>/<video>)
            this.scanAndBridgeInternalAudio();
            this.audioCheckInterval = setInterval(() => this.scanAndBridgeInternalAudio(), 500);

            if (typeof document !== 'undefined' && document.body) {
                this.domObserver = new MutationObserver(() => this.scanAndBridgeInternalAudio());
                this.domObserver.observe(document.body, { childList: true, subtree: true });
            }

            return this.audioDestination.stream.getAudioTracks();
        } catch (err) {
            console.warn('[WebRecordingAudioMixer] Khởi tạo AudioMixer thất bại:', err);
            return [];
        }
    }

    private bridgeForeignAudioNode(foreignNode: AudioNode): void {
        if (!this.audioContext || !this.audioDestination || !foreignNode) return;
        if (this.activeBridges.has(foreignNode)) return;

        try {
            if (this.audioContext.state === 'suspended') {
                this.audioContext.resume().catch(() => {});
            }
            const foreignCtx = foreignNode.context as AudioContext;
            if (!foreignCtx || typeof foreignCtx.createMediaStreamDestination !== 'function') return;
            if (foreignCtx.state === 'suspended' && typeof foreignCtx.resume === 'function') {
                foreignCtx.resume().catch(() => {});
            }

            if (foreignCtx === this.audioContext) {
                foreignNode.connect(this.audioDestination);
                this.activeBridges.set(foreignNode, { dest: this.audioDestination, source: null as any });
                return;
            }

            const foreignDest = foreignCtx.createMediaStreamDestination();
            foreignNode.connect(foreignDest);
            const localSource = this.audioContext.createMediaStreamSource(foreignDest.stream);
            localSource.connect(this.audioDestination);
            this.activeBridges.set(foreignNode, { dest: foreignDest, source: localSource });
        } catch (err) {
            console.warn('[WebRecordingAudioMixer] Lỗi khi bridge foreign AudioNode:', err);
        }
    }

    private bridgeMediaElement(el: HTMLMediaElement): void {
        if (!this.audioContext || !this.audioDestination || this.connectedElements.has(el)) return;
        try {
            const captureFn = (el as any).captureStream || (el as any).mozCaptureStream;
            if (typeof captureFn === 'function') {
                const stream: MediaStream = captureFn.call(el);
                if (stream && stream.getAudioTracks().length > 0) {
                    const source = this.audioContext.createMediaStreamSource(stream);
                    source.connect(this.audioDestination);
                    this.activeStreamSources.push(source);
                    this.connectedElements.add(el);
                }
            }
        } catch {
            // Bỏ qua nếu phần tử media khác nguồn và chưa có CORS
        }
    }

    private scanAndBridgeInternalAudio(): void {
        if (typeof window === 'undefined' || !this.audioContext || !this.audioDestination) return;

        // Khi displayStream đã có Tab Audio track, toàn bộ âm thanh từ tab (Howler, DOM audio, AI)
        // đã được thu trọn vẹn từ luồng âm thanh gốc của trình duyệt. Không nối lặp lại Howler và DOM elements
        // để triệt tiêu độ lệch trễ 50ms gây hiện tượng vang vọng (Echo / Comb Filtering).
        if (this.hasDisplayAudio) {
            return;
        }

        // 1. Giọng nói AI Agent (Gemini Live AudioStreamer)
        const geminiStreamer = (window as any).__geminiAudioStreamer;
        if (geminiStreamer?.gainNode) {
            this.bridgeForeignAudioNode(geminiStreamer.gainNode);
        }

        // 2. Nhạc nền và hiệu ứng âm thanh (Howler.js)
        const howlerGlobal = (window as any).Howler;
        if (howlerGlobal?.masterGain) {
            this.bridgeForeignAudioNode(howlerGlobal.masterGain);
        }

        // 3. Các thẻ <audio> và <video> trên DOM (bao gồm OpenAI Realtime WebRTC audio & popup video)
        if (typeof document !== 'undefined') {
            const mediaElements = document.querySelectorAll<HTMLMediaElement>('audio, video');
            mediaElements.forEach((el) => this.bridgeMediaElement(el));
        }
    }

    public cleanup(): void {
        if (this.audioCheckInterval) {
            clearInterval(this.audioCheckInterval);
            this.audioCheckInterval = null;
        }
        if (this.domObserver) {
            this.domObserver.disconnect();
            this.domObserver = null;
        }
        if (this.keepAliveOscillator) {
            try {
                this.keepAliveOscillator.stop();
                this.keepAliveOscillator.disconnect();
            } catch {}
            this.keepAliveOscillator = null;
        }
        if (this.keepAliveGain) {
            try {
                this.keepAliveGain.disconnect();
            } catch {}
            this.keepAliveGain = null;
        }
        this.activeBridges.forEach(({ dest, source }, foreignNode) => {
            try {
                if (dest && dest !== this.audioDestination) foreignNode.disconnect(dest);
            } catch {}
            try {
                if (source) source.disconnect();
            } catch {}
        });
        this.activeBridges.clear();
        this.activeStreamSources.forEach((src) => {
            try {
                src.disconnect();
            } catch {}
        });
        this.activeStreamSources = [];
        if (this.micStream) {
            this.micStream.getTracks().forEach((t) => t.stop());
            this.micStream = null;
        }
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close().catch(() => {});
        }
        this.audioContext = null;
        this.audioDestination = null;
    }
}
